import { observer } from 'mobx-react';
import FontBody3 from '../../../components/fonts/font-body3/font-body3';
import { FONT_COLOR } from '../../../components/fonts/font-props';
import useDashboardTranslation from '../../../dashboard-translation/hooks/dashboard-translation-hook';
import CustomerDataCardAddressValue from './customer-data-card-address-value';
import RmaShopperBlurredPlaceholder, {
    MASKED_SHIPPING_ADDRESS_PLACEHOLDER,
} from '../../manage-returns/rma-page/components/rma-customer-data-card/rma-shopper-blurred-placeholder';
import RmaShopperLoadFailed from '../../manage-returns/rma-page/components/rma-customer-data-card/rma-shopper-load-failed';
import RmaShopperRevealButton from '../../manage-returns/rma-page/components/rma-customer-data-card/rma-shopper-reveal-button';
import RmaShopperDetailsStore from '../../manage-returns/rma-page/stores/rma-shopper-details-store';
import { LOAD_STATE } from '../../../types/load-state';

interface IProps {
    shopperDetails: RmaShopperDetailsStore;
    addressId?: string | null;
    isGift?: boolean;
}

/** Shipping address rows for internal admins on the RMA page (masked until Reveal). */
const CustomerDataCardShopperShippingSection: React.FC<IProps> = observer(({
    shopperDetails,
    addressId,
    isGift,
}) => {
    const pageScope = 'dashboard-create-rma';
    const { getTranslation } = useDashboardTranslation();
    const loadState = shopperDetails.shippingAddressLoadState;
    const isNotRequired = addressId === null && isGift;

    let addressCell: React.ReactNode;

    if (isNotRequired) {
        addressCell = (
            <CustomerDataCardAddressValue isAddressRequired={false} />
        );
    } else if (loadState === LOAD_STATE.FAILED) {
        addressCell = (
            <div className="customer-data-card-address-container">
                <RmaShopperLoadFailed
                    message={getTranslation('SHOPPER_FIELD_LOAD_FAILED', pageScope)}
                    retryLabel={getTranslation('RETRY', pageScope)}
                    onRetry={() => void shopperDetails.loadShopperAddress()}
                />
            </div>
        );
    } else if (loadState === LOAD_STATE.LOADED) {
        addressCell = (
            <CustomerDataCardAddressValue address={shopperDetails.shippingAddress} />
        );
    } else {
        addressCell = (
            <div className="customer-data-card-address-container">
                <span className="rma-shopper-masked-value">
                    <RmaShopperBlurredPlaceholder text={MASKED_SHIPPING_ADDRESS_PLACEHOLDER} />
                </span>
                <RmaShopperRevealButton
                    label={getTranslation('REVEAL', pageScope)}
                    disabled={loadState === LOAD_STATE.LOADING}
                    onClick={() => void shopperDetails.loadShopperAddress()}
                />
            </div>
        );
    }

    return (
        <>
            <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('SHIPPING_ADDRESS', pageScope)} </FontBody3>
            {addressCell}
            {shopperDetails.updatedShippingAddress && (
                <>
                    <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('UPDATED_ADDRESS', pageScope)}</FontBody3>
                    <CustomerDataCardAddressValue address={shopperDetails.updatedShippingAddress} />
                </>
            )}
        </>
    );
});

export default CustomerDataCardShopperShippingSection;
